/**
 * Homepage sidebar.
 * Service Desk is the queue. Operate is how you work with Theo. Agents are the engineers who do the work.
 */

import { THEO_PILOT_LOCKED_NAV_IDS, THEO_PILOT_UNAVAILABLE_MESSAGE } from "../utils/theoPilotAccess";
import type { TheoAgentNav } from "./theoAgents";
import {
  THEO_OVERVIEW_PATH,
  SectionTabDef,
  buildAdministrationTabs,
  buildAgentTabs,
  buildDeveloperTabs,
  buildHelpTabs,
  buildInsightsTabs,
  buildTheoTabs,
} from "./sectionTabDefs";

export type NavMode = "full" | "psa-setup" | "analytics-sku";

export interface FlatNavAccess {
  mode: NavMode;
  hasWorkScopes: boolean;
  showAutomation: boolean;
  showDashboard: boolean;
  showUsage: boolean;
  showObserver: boolean;
  showAuditLogs: boolean;
}

export interface FlatNavItem {
  id: string;
  label: string;
  path: string;
}

export interface SidebarSection extends FlatNavItem {
  children: SectionTabDef[];
}

/** Panel flags for Insights children. Usage and Reports follow the section gates. */
export interface SidebarChildAccess {
  workflows: boolean;
  schedules: boolean;
  playbooks: boolean;
  billing: boolean;
  technician: boolean;
  ticketAnalysis: boolean;
  usageInsights: boolean;
  ticketLifecycle: boolean;
  /** LaunchDarkly `theo-pilot`. Locks Workflows, Playbooks, Schedules, and Create an Agent. */
  theoPilot: boolean;
}

export function navModeFromAccess(psaSetupOnly: boolean, mspSku?: string | null): NavMode {
  if (psaSetupOnly) return "psa-setup";
  if (mspSku === "analytics") return "analytics-sku";
  return "full";
}

export function buildFlatNav(access: FlatNavAccess): FlatNavItem[] {
  const items: FlatNavItem[] = [];
  const full = access.mode === "full";
  const activityAllowed = access.mode === "full" || access.mode === "analytics-sku";

  if (full && access.hasWorkScopes) {
    items.push({ id: "work", label: "Service Desk", path: "/supervisor" });
  }
  if (access.showAutomation) {
    items.push({ id: "agents", label: "Agents", path: "/theo-automations/monitor" });
  }
  if (full) {
    items.push({ id: "theo", label: "Operate", path: "/chat-assistant" });
  }

  const showUsage = activityAllowed && access.showUsage;
  // Devices is available to every workspace, even when the other Insights panels are gated.
  const insightsPath = access.showDashboard ? THEO_OVERVIEW_PATH : showUsage ? "/usage" : "/msp-analytics?panel=devices";
  items.push({ id: "insights", label: "Insights", path: insightsPath });

  items.push({ id: "administration", label: "Administration", path: "/accounts" });

  const showObserver = full && access.showObserver;
  if (showObserver) {
    items.push({
      id: "developer",
      label: "Tools",
      path: "/observer",
    });
  }

  items.push({ id: "help", label: "Help", path: "/help-center/guides" });
  return items;
}

export function buildSidebarSections(
  access: FlatNavAccess,
  children: SidebarChildAccess,
  /** The live agent roster. Omitted before GET /agents answers, which falls back to the default. */
  agents?: TheoAgentNav[]
): SidebarSection[] {
  const activityAllowed = access.mode === "full" || access.mode === "analytics-sku";
  const full = access.mode === "full";

  return buildFlatNav(access).map((item) => {
    let sectionChildren: SectionTabDef[] = [];
    if (item.id === "theo") {
      sectionChildren = buildTheoTabs({
        workflows: full && children.workflows,
        schedules: full && children.schedules,
        playbooks: full && children.playbooks,
      });
    } else if (item.id === "agents") {
      sectionChildren = buildAgentTabs(agents);
    } else if (item.id === "insights") {
      sectionChildren = buildInsightsTabs({
        theoAnalytics: access.showDashboard,
        technician: children.technician,
        ticketAnalysis: children.ticketAnalysis,
        usageInsights: children.usageInsights,
        ticketLifecycle: children.ticketLifecycle,
        showUsage: activityAllowed && access.showUsage,
      });
    } else if (item.id === "administration") {
      sectionChildren = buildAdministrationTabs({
        playbooks: !full && children.playbooks,
        billing: activityAllowed && children.billing,
      });
    } else if (item.id === "developer") {
      sectionChildren = buildDeveloperTabs({
        observer: full && access.showObserver,
      });
    } else if (item.id === "help") {
      sectionChildren = buildHelpTabs({
        auditLogs: activityAllowed && access.showAuditLogs,
      });
    }
    return { ...item, children: applyTheoPilotLock(sectionChildren, children.theoPilot) };
  });
}

function applyTheoPilotLock(tabs: SectionTabDef[], theoPilot: boolean): SectionTabDef[] {
  if (!theoPilot) return tabs;
  return tabs.map((tab) =>
    THEO_PILOT_LOCKED_NAV_IDS.has(tab.id) ? { ...tab, disabledReason: THEO_PILOT_UNAVAILABLE_MESSAGE } : tab
  );
}

function startsWithPath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function isFlatNavActive(id: string, pathname: string): boolean {
  switch (id) {
    case "work":
      return startsWithPath(pathname, "/supervisor");
    case "theo":
      return (
        startsWithPath(pathname, "/chat-assistant") ||
        startsWithPath(pathname, "/deep-research") ||
        startsWithPath(pathname, "/custom_workflows") ||
        startsWithPath(pathname, "/schedules") ||
        startsWithPath(pathname, "/playbooks") ||
        startsWithPath(pathname, "/application-skills")
      );
    case "agents":
      return (
        startsWithPath(pathname, "/theo-automations") ||
        startsWithPath(pathname, "/ai-automations") ||
        startsWithPath(pathname, "/agents")
      );
    case "insights":
      return (
        pathname === "/msp-analytics" ||
        pathname === "/dashboard" ||
        startsWithPath(pathname, THEO_OVERVIEW_PATH) ||
        startsWithPath(pathname, "/usage") ||
        startsWithPath(pathname, "/research-reports") ||
        startsWithPath(pathname, "/report")
      );
    case "administration":
      return (
        startsWithPath(pathname, "/accounts") ||
        startsWithPath(pathname, "/theo-users") ||
        startsWithPath(pathname, "/integrations") ||
        startsWithPath(pathname, "/billing") ||
        pathname === "/configuration" ||
        startsWithPath(pathname, "/app-configuration") ||
        startsWithPath(pathname, "/platform-configuration")
      );
    case "developer":
      return pathname === "/observer" || startsWithPath(pathname, "/observer");
    case "help":
      return (
        startsWithPath(pathname, "/help-center") ||
        startsWithPath(pathname, "/documentation") ||
        pathname === "/audit-logs"
      );
    default:
      return false;
  }
}
